import {
  Pagination,
  Table,
  useTableHelpers,
  useTablePagination,
} from '@teamsnap/snap-ui';
import {
  LoadingType,
  useOrderDetails,
  useOrderDetailsActions,
} from 'hooks/orderDetails';
import * as React from 'react';
import { useState } from 'react';
import OrderDetailsTableSkeleton from '../order-table-skeleton';
import {
  formatChargebackRow,
  formatPaymentTransaction,
  formatRefundRow,
  formatRow,
  getColumns,
} from './helpers';
import { RefundTransactionModal } from './refund-transaction-modal';
import { TransactionGroup, TransactionRow } from './types';

function getNestedRows(transaction: TransactionGroup) {
  if (!transaction.refunds?.length && !transaction.chargebacks?.length) {
    return [];
  }
  return [
    formatPaymentTransaction(transaction, false),
    ...transaction.refunds.map((refund) =>
      formatRefundRow(refund, transaction, false)
    ),
    ...transaction.chargebacks.map((chargeback) =>
      formatChargebackRow(chargeback, transaction, false)
    ),
  ].flat();
}

export const TransactionsTable = () => {
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [transactionToRefund, setTransactionToRefund] = useState<
    TransactionRow | undefined
  >(undefined);

  const { order, transactions, loading } = useOrderDetails();
  const { getTransactions } = useOrderDetailsActions();

  React.useEffect(() => {
    if (!transactions || !transactions.length) {
      getTransactions(order?.id);
    }
  }, [transactions, order]);

  const parsedData: TransactionRow[] = React.useMemo(() => {
    if (!transactions || !transactions.length) return [];
    return transactions.map((transaction) => {
      const nestedRows = getNestedRows(transaction);
      const formattedRow = formatRow(transaction);
      if (nestedRows.length) {
        return { ...formattedRow, expandedRows: nestedRows };
      }
      return formattedRow;
    });
  }, [transactions]);

  const handleOpenRefundModal = (transaction: TransactionRow) => {
    setTransactionToRefund(transaction);
    setIsRefundModalOpen(true);
  };

  const handleCloseRefundModal = () => {
    setTransactionToRefund(undefined);
    setIsRefundModalOpen(false);
  };

  const { rows, headerRow } = useTableHelpers({
    data: parsedData,
    columns: getColumns(handleOpenRefundModal),
    initialSorting: {
      method: 'date',
      sortKey: 'date',
      acsending: true,
    },
  });

  const { currentPage, onPageChange, totalCount, currentRows, pageSize } =
    useTablePagination({
      rows,
      pageSize: 10,
    });

  if (loading[LoadingType.GetTransactions])
    return <OrderDetailsTableSkeleton />;

  return (
    <div>
      <Table
        data-testid="transactions-table--table"
        headerRow={headerRow}
        rows={currentRows}
      />
      <Pagination
        className="sui-mt-3"
        {...{ currentPage, onPageChange, totalCount, pageSize }}
      />
      {transactionToRefund && (
        <RefundTransactionModal
          transaction={transactionToRefund}
          closeFn={handleCloseRefundModal}
          isOpen={isRefundModalOpen}
        />
      )}
    </div>
  );
};

export default TransactionsTable;
